import { useState } from 'react'
import type { JSX } from 'react'
import { useTranslation } from 'react-i18next'
import { LanguagePicker } from '@rs/ui'

interface LoginOk { user: { role: string; source: string }; mustChangePass: boolean }
interface LoginError { message: string }

/**
 * ══ CỔNG ĐĂNG NHẬP THẦU PHỤ (E15) ═════════════════════════════════════════════════════════════
 * Tách HẲN khỏi cổng nhân viên nội bộ (`/login`: FADO SSO hoặc tài khoản `app_user` — E13 sửa 19/10/2026). Thầu phụ
 * (bảng `vendor_user`, mật khẩu argon2id do rs-erp tự quản) đăng nhập ở ĐÂY; hai bảng không trộn — tài khoản nội bộ gõ
 * vào đây bị từ chối và ngược lại.
 * ⚠ KHÔNG có ô chọn vai, KHÔNG có nút "Xem thử" — vai của đường này LUÔN là `ncc`, máy chủ tự gán
 *   (xem `vendorLogin()`/`resolveSession()` ở `@rs/identity`), không nhận từ client.
 *
 * ⚠ Ở nhánh Task 16c, kho CHƯA có `useAuth()`/i18n (`react-i18next`)/`LanguagePicker` — hạ tầng
 *   dùng chung đó chưa được dựng. Trang này vì vậy tự quản state bằng `useState` và gọi thẳng
 *   `fetch()`, đúng khuôn `HomePage`/`HealthAccountingPage`.
 *   ⭐ TASK 23 nối `<LanguagePicker />` vào — spec D03 §6.1 nói rõ `/ncc/login` dùng *"cùng khung và
 *   cùng `<LanguagePicker>`"* như `/login`. Thầu phụ cũng đổi được ngôn ngữ TRƯỚC khi đăng nhập
 *   (giữ tạm ở `localStorage`, `auth.tsx` đẩy lên phiên sau khi vào — `vendor_user.lang`, 0014).
 *   ⭐ D07b ĐÃ RÚT SANG KHO CHUỖI (nợ `D03.md §4.4`). Ba nhãn dùng lại khoá SẴN CÓ của bản cũ
 *   (`login.email` · `login.password` · `login.signIn`); chỉ ba chuỗi thật sự riêng của cổng này
 *   là khoá mới (`login.ncc.title` · `.submitting` · `.netError`) — bản `vi` chính là câu chữ đang
 *   chạy, `en`/`zh` do máy dịch và ĐANG CHỜ ANH DUYỆT (ghi ở `docs/ban-giao/D07b.md`).
 *
 * ⚠ Lỗi hiện bằng MỘT KHỐI VĂN BẢN (`role="alert"`) ngay trong trang — KHÔNG `alert()`/`confirm()`
 *   (luật lint `no-restricted-syntax` cấm — không test tự động được, không đọc được trên điện
 *   thoại) và KHÔNG tự dựng modal riêng.
 *   ⭐ CẬP NHẬT SAU TASK 19: `<AlertModal>`/`<ConfirmModal>` của `@rs/ui` ĐÃ DỰNG (quyết định C1),
 *   nhưng trang này CỐ Ý VẪN dùng khối `role="alert"` tại chỗ — lỗi đăng nhập nằm NGAY CẠNH ô vừa
 *   gõ sai thì đọc tiện hơn một hộp thoại chặn phải bấm mới đi tiếp được. Đổi sang modal chỉ để
 *   "có nơi dùng" là tạo nơi dùng giả tạo; hai component đó phục vụ 833 chỗ gọi `alert()`/
 *   `confirm()` của bản cũ, thay dần từ D04+.
 * ⚠ Đăng nhập xong mà `mustChangePass` bật (mật khẩu tạm, E17): trang chỉ HIỆN CỜ đó, KHÔNG tự
 *   dựng luồng đổi mật khẩu (API `POST /api/v1/ncc/auth/doi-mat-khau` đã có ở `@rs/identity`/
 *   `vendor-auth.ts`).
 *   ⭐ SỬA CON TRỎ TREO (rà lại ở Task 19): chú thích cũ chỉ sang "Task 19" — SAI. Task 19 dựng
 *   khung giao diện + sidebar + màn đăng nhập nội bộ, KHÔNG dựng màn đổi mật khẩu thầu phụ và chưa
 *   bao giờ được giao việc đó. Kế hoạch có nhắc đường dẫn `/ncc/doi-mat-khau`
 *   (`plans/2026-09-03-dot-0-nen-tang.md:5501`) nhưng KHÔNG task nào của ĐỢT 0 dựng nó — màn đó
 *   thuộc **cổng thầu phụ thật ở D04+**, cùng lúc với `/ncc` (NccPortal).
 */
export const VendorLoginPage = (): JSX.Element => {
  const { t } = useTranslation()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [err, setErr] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const submit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault()
    if (submitting) return
    setErr(null)
    setSubmitting(true)
    try {
      const res = await fetch('/api/v1/ncc/auth/login', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ email, password }),
      })
      if (!res.ok) {
        const errBody = (await res.json()) as LoginError
        setErr(errBody.message)
        return
      }
      const ok = (await res.json()) as LoginOk
      if (ok.mustChangePass) {
        // Mật khẩu TẠM (E17) ⇒ ÉP đổi trước khi vào cổng. Màn đổi là `/ncc/doi-mat-khau` (D06).
        // `?bat-buoc=1` để màn kia biết đây là luồng ÉP đổi (E17) mà hiện cảnh báo —
        // nó không đọc được cờ `mustChangePass` của lượt đăng nhập này.
        location.assign('/ncc/doi-mat-khau?bat-buoc=1')
        return
      }
      // ⚠ `/ncc`, KHÔNG phải `/`: `canAccessRoute('ncc', '/')` là `false` nên router đá tiếp sang
      //   `/ncc` — một vòng tải THỪA người dùng nhìn thấy. Nợ ghi ở `D03.md §4.4`, trả ở D07b.
      location.assign('/ncc')
    } catch {
      setErr(t('login.ncc.netError'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    /* Khung nhận diện giống `login.tsx` — xem khối chú thích ở đó. Cổng thầu phụ là màn của NGƯỜI
       NGOÀI công ty, nên càng không được để trần. Ô email/mật khẩu giữ nguyên (E15: thầu phụ có
       tài khoản riêng, không đi qua FADO SSO). Nền gradient + căn giữa dọc — `ps:Login.tsx:150-151`. */
    <main className="min-h-full grid place-items-center bg-gradient-to-br from-brand-50 via-white to-sky-100 p-4">
      <div className="w-full max-w-sm">
        <header className="mb-3 flex justify-end">
          <LanguagePicker tone="light" />
        </header>
        <div className="overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-xl">
          <div className="bg-gradient-to-r from-brand-500 to-sky-500 px-6 py-7 text-center">
            <img src="/RT-LOGO-header.jpg" alt={t('app.name')}
                 className="mx-auto mb-2 h-12 w-auto rounded-lg bg-white object-contain px-2 py-1" />
            <div className="text-sm text-brand-50">{t('app.name')}</div>
          </div>
          <form onSubmit={(e) => void submit(e)} className="space-y-4 px-6 py-6">
            <h1 className="text-lg font-bold text-slate-800">{t('login.ncc.title')}</h1>
            <div>
              <label htmlFor="ncc-email" className="mb-1 block text-xs font-semibold text-slate-500">{t('login.email')}</label>
              <input
                id="ncc-email" type="email" value={email} autoComplete="username" required
                onChange={(e) => { setEmail(e.target.value) }}
                className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-100"
              />
            </div>
            <div>
              <label htmlFor="ncc-pw" className="mb-1 block text-xs font-semibold text-slate-500">{t('login.password')}</label>
              <input
                id="ncc-pw" type="password" value={password} autoComplete="current-password" required
                onChange={(e) => { setPassword(e.target.value) }}
                className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-100"
              />
            </div>
            {err !== null && err !== '' && (
              <p role="alert" className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-[13px] text-rose-700">{err}</p>
            )}
            <button type="submit" disabled={submitting}
                    className="w-full rounded-lg bg-gradient-to-r from-brand-500 to-brand-600 px-4 py-2.5 text-sm font-bold text-white hover:from-brand-600 hover:to-brand-700 disabled:opacity-40">
              {submitting ? t('login.ncc.submitting') : t('login.signIn')}
            </button>
          </form>
        </div>
      </div>
    </main>
  )
}
